import { BALL, RULES } from './config.js';
import { LEVELS } from '../levels/levels.js';
import { buildField } from '../entities/brickfield.js';
import { createBall, stickBall } from '../entities/ball.js';
import { createDoh } from '../entities/doh.js';
import { createPaddle, resetPaddle } from '../entities/paddle.js';
import { clearPaddleEffects } from '../systems/powerups.js';
import { createParticles } from '../systems/particles.js';

/**
 * The mutable state of one playthrough: score, lives, the current round and
 * every entity on the field. Kept apart from the state machine so the round
 * setup rules live in one readable place.
 */

export function createSession({ reducedMotion = false } = {}) {
  return {
    score: 0,
    /** Attract mode plays the game but must not bank points. */
    scoring: true,
    lives: RULES.startingLives,
    round: 1,
    level: null,
    isBoss: false,
    backdropIndex: 0,

    field: null,
    doh: null,
    paddle: createPaddle(),
    paddleVisible: true,
    balls: [],
    capsules: [],
    bolts: [],
    dohShots: [],
    particles: createParticles({ reducedMotion }),

    /** Break capsule state. */
    warpOpen: false,
    warpPhase: 0,
    warping: false,

    /** Speed ramp bookkeeping. */
    rampHits: 0,
    roundTime: 0,
    /** Seconds since the last brick was struck, used by the stalemate guard. */
    sinceBrick: 0,
    bricksBroken: 0,
    capsuleWarmup: 0,
    fireCooldown: 0,

    nextExtraLife: RULES.firstExtraLife,
    banner: null,
    toast: null,
    stats: { bricks: 0, capsules: 0, rounds: 0 },
  };
}

/** Load a round's map and reset everything that is per-round. */
export function startRound(session, roundNumber) {
  const index = Math.min(roundNumber, LEVELS.length) - 1;
  const level = LEVELS[index];

  session.round = roundNumber;
  session.level = level;
  session.isBoss = Boolean(level.boss);
  session.backdropIndex = index;
  session.field = buildField(level, roundNumber);
  session.doh = level.boss ? createDoh() : null;

  session.balls.length = 0;
  session.capsules.length = 0;
  session.bolts.length = 0;
  session.dohShots.length = 0;
  session.particles.clear();

  session.warpOpen = false;
  session.warpPhase = 0;
  session.warping = false;
  session.rampHits = 0;
  session.roundTime = 0;
  session.sinceBrick = 0;
  session.capsuleWarmup = 0;
  session.fireCooldown = 0;
  session.paddleVisible = true;

  clearPaddleEffects(session.paddle);
  resetPaddle(session.paddle);
  serveBall(session);
}

/** Put a fresh ball on the paddle, ready to launch. */
export function serveBall(session) {
  const speed = Math.min(
    BALL.maxSpeed,
    BALL.baseSpeed + (session.round - 1) * BALL.speedPerRound,
  );
  const ball = createBall({
    x: session.paddle.cx,
    y: session.paddle.y - BALL.radius - 1,
    speed,
    angle: -Math.PI / 2 + (Math.random() < 0.5 ? -0.3 : 0.3),
  });
  stickBall(ball, session.paddle);
  // The opening ball always waits for a deliberate launch.
  ball.stuckTimer = -Infinity;
  session.balls.push(ball);
  return ball;
}

/** Reset the field state after losing a life, keeping round progress. */
export function resetAfterLife(session) {
  session.capsules.length = 0;
  session.bolts.length = 0;
  session.dohShots.length = 0;
  session.warpOpen = false;
  session.warpPhase = 0;
  session.warping = false;
  session.rampHits = 0;
  session.sinceBrick = 0;
  session.fireCooldown = 0;
  session.roundTime = Math.max(0, session.roundTime - 30);
  clearPaddleEffects(session.paddle);
  resetPaddle(session.paddle);
  serveBall(session);
}

/** Award points and hand back true when the score crossed an extra-life mark. */
export function addScore(session, points) {
  if (!session.scoring) return false;
  session.score += points;
  if (session.score >= session.nextExtraLife && session.lives < RULES.maxLives) {
    session.nextExtraLife += RULES.extraLifeInterval;
    session.lives += 1;
    return true;
  }
  return false;
}

export function showToast(session, text, duration = 1.8) {
  session.toast = { text, timer: duration };
}

export function tickToast(session, dt) {
  if (!session.toast) return;
  session.toast.timer -= dt;
  if (session.toast.timer <= 0) session.toast = null;
}
